import React from "react";
import PillSelector from "./ui/PillSelector";
import ToggleSwitch from "./ui/ToggleSwitch";
import InputWithSuffix from "./ui/InputWithSuffix";

const LOAN_TYPE_OPTIONS = [
  { value: "SPITZER", label: "שפיצר" },
  { value: "BULLET", label: "בולט" },
  { value: "AMORTIZING", label: "Amortizing" },
];

const PAYMENT_PERIOD_OPTIONS = [
  { value: "WEEKLY", label: "שבועי" },
  { value: "MONTHLY", label: "חודשי" },
  { value: "QUARTERLY", label: "רבעוני" },
  { value: "SEMI_ANNUALLY", label: "חצי שנתי" },
  { value: "ANNUALLY", label: "שנתי" },
];

/**
 * Tab 2 — עמלות ותנאים (Fees & Terms)
 */
export default function LoanFormTab2({
  loan,
  displayValues,
  errors,
  touched,
  onChange,
  onBlur,
  onBlurNumber,
}) {
  return (
    <div className="form-grid">
      {/* ── Credit Facility Banner ───────────────────────── */}
      {loan.classification === "CREDIT_FACILITY" && (
        <div className="credit-facility-banner" style={{ gridColumn: "1 / -1" }}>
          <span className="credit-facility-banner-icon">💳</span>
          <div>
            <div className="credit-facility-banner-title">מסגרת אשראי</div>
            <div className="credit-facility-banner-body">סכום הנמשך כרגע קובע את בסיס חישוב הריבית. עמלת אי-ניצול חלה על היתרה הפנויה.</div>
          </div>
        </div>
      )}

      {/* ── Fees ─────────────────────────────────────────── */}
      <h3 className="form-section-header">עמלות</h3>

      <div className="field">
        <label className="label-optional">עמלת הקצאה <span className="optional-badge">(אופציונלי)</span></label>
        <InputWithSuffix
          name="originationFeePct"
          value={displayValues.originationFeePct}
          onChange={onChange}
          onBlur={onBlurNumber}
          placeholder="0"
        />
      </div>

      <div className="field">
        <label className="label-optional">עמלת מסגרת לא משוכה (עמלת אי-ניצול) <span className="optional-badge">(אופציונלי)</span></label>
        <InputWithSuffix
          name="unusedCommitmentFeePct"
          value={displayValues.unusedCommitmentFeePct}
          onChange={onChange}
          onBlur={onBlurNumber}
          placeholder="0"
        />
      </div>

      <div className="field">
        <label className="label-optional">קנס פירעון מוקדם <span className="optional-badge">(אופציונלי)</span></label>
        <InputWithSuffix
          name="prepaymentPenaltyPct"
          value={displayValues.prepaymentPenaltyPct}
          onChange={onChange}
          onBlur={onBlurNumber}
          placeholder="0"
        />
      </div>

      <div className="field">
        <label className="label-optional">דמי ניהול הלוואה <span className="optional-badge">(אופציונלי)</span></label>
        <InputWithSuffix
          name="managementFeePct"
          value={displayValues.managementFeePct}
          onChange={onChange}
          onBlur={onBlurNumber}
          placeholder="0"
        />
      </div>

      {/* ── Credit Facility: Drawn Amount ────────────────── */}
      {loan.classification === "CREDIT_FACILITY" && (
        <>
          <h3 className="form-section-header">סכום נמשך</h3>
          <div className="field form-full-width">
            <label className="label-optional">
              סכום נמשך כרגע (מסגרת אשראי) <span className="optional-badge">(אופציונלי)</span>
            </label>
            <input
              className="input"
              name="drawnAmount"
              type="text"
              value={displayValues.drawnAmount ?? "0"}
              onChange={onChange}
              onBlur={onBlurNumber}
              placeholder="0"
            />
            <span className="field-hint">
              כמה מהמסגרת בשימוש כרגע. ריבית מחושבת רק על סכום זה.
            </span>
          </div>
        </>
      )}

      {/* ── Repayment Type ───────────────────────────────── */}
      <h3 className="form-section-header">סוג ותדירות</h3>

      <div className="field form-full-width">
        <label className="label-optional">סוג הלוואה <span className="optional-badge">(אופציונלי)</span></label>
        <PillSelector
          name="loanType"
          value={loan.loanType}
          options={LOAN_TYPE_OPTIONS}
          onChange={onChange}
        />
      </div>

      <div className="field form-full-width">
        <label className="label-optional">תקופת תשלום <span className="optional-badge">(אופציונלי)</span></label>
        <PillSelector
          name="paymentPeriod"
          value={loan.paymentPeriod}
          options={PAYMENT_PERIOD_OPTIONS}
          onChange={onChange}
        />
      </div>

      {/* ── Options ──────────────────────────────────────── */}
      <h3 className="form-section-header">אופציות</h3>

      <div className="field">
        <ToggleSwitch
          name="hasEarlyRepaymentOption"
          checked={loan.hasEarlyRepaymentOption}
          onChange={onChange}
          label="אופציה לפירעון מוקדם"
        />
      </div>

      <div className="field">
        <ToggleSwitch
          name="canExtend"
          checked={loan.canExtend}
          onChange={onChange}
          label="אופציה להארכה"
        />
      </div>

      {loan.hasEarlyRepaymentOption && (
        <div className="conditional-fields">
          <div className="conditional-fields-inner">
            <div className="field">
              <label className="label-required">ימי הודעה מראש לפירעון מוקדם</label>
              <input
                className="input"
                name="earlyRepaymentNoticeDays"
                type="text"
                value={displayValues.earlyRepaymentNoticeDays}
                onChange={onChange}
                onBlur={onBlurNumber}
                placeholder="0"
              />
              {touched.earlyRepaymentNoticeDays && errors.earlyRepaymentNoticeDays && (
                <div className="field-error">{errors.earlyRepaymentNoticeDays}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Additional Details ───────────────────────────── */}
      <h3 className="form-section-header">פרטים נוספים</h3>

      <div className="field">
        <label className="label-optional">פרויקט <span className="optional-badge">(אופציונלי)</span></label>
        <input
          className="input"
          name="projectName"
          value={loan.projectName}
          onChange={onChange}
        />
      </div>

      <div className="field form-full-width">
        <ToggleSwitch
          name="hasRestrictedElement"
          checked={loan.hasRestrictedElement}
          onChange={onChange}
          label="יש חלק מוגבל שלא ניתן למשוך (ערבות ופקדונות)"
        />
      </div>

      {loan.hasRestrictedElement && (
        <div className="conditional-fields">
          <div className="conditional-fields-inner">
            <div className="field">
              <label className="label-required">סכום החלק המוגבל</label>
              <input
                className="input"
                name="restrictedElementAmount"
                type="text"
                value={displayValues.restrictedElementAmount ?? ""}
                onChange={onChange}
                onBlur={onBlurNumber}
                placeholder="0"
              />
              {touched.restrictedElementAmount && errors.restrictedElementAmount && (
                <div className="field-error">{errors.restrictedElementAmount}</div>
              )}
            </div>

            <div className="field">
              <label className="label-required">סיבת ההגבלה</label>
              <input
                className="input"
                name="restrictedElementReason"
                value={loan.restrictedElementReason}
                onChange={onChange}
                onBlur={onBlur}
                placeholder="למשל: ערבות ופקדונות"
              />
              {touched.restrictedElementReason && errors.restrictedElementReason && (
                <div className="field-error">{errors.restrictedElementReason}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
